import * as React from "react";
import { ArrowLeft, FileText } from "lucide-react";

import { PdfSourcePreview } from "../../../components/PdfSourcePreview";
import { SimpleMarkdown } from "../../../components/SimpleMarkdown";
import type { DocumentSourceItem } from "../../ai/sourceItems";
import { HtmlSourcePreview } from "./HtmlSourcePreview";
import {
  SOURCE_TABLE_CLASS_NAME,
  SOURCE_TD_CLASS_NAME,
  SOURCE_TH_CLASS_NAME,
} from "./sourceTableClasses";

interface PdfBackedDocumentRendererProps {
  item: DocumentSourceItem;
  content: string;
  pdfSrc: string;
  highlightUuid: string | null;
  citationColor: string;
  hasStructuredHtml: boolean;
}

export const PdfBackedDocumentRenderer: React.FC<PdfBackedDocumentRendererProps> = ({
  item,
  content,
  pdfSrc,
  highlightUuid,
  citationColor,
  hasStructuredHtml,
}) => {
  const [view, setView] = React.useState<"extracts" | "document">("extracts");
  const title = item.link.metadata?.documentName ?? "Full document";

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      {view === "extracts" ? (
        <>
          <div className="factset-scrollbar min-h-0 flex-1 overflow-auto px-1 py-5">
            <article className="prose-citation citation-markdown-preview mx-auto max-w-[760px] bg-background">
              <DocumentExtractContent
                content={content}
                highlightUuid={highlightUuid}
                citationColor={citationColor}
                hasStructuredHtml={hasStructuredHtml}
                title={title}
                sourceName={item.link.metadata?.companyName}
              />
            </article>
          </div>
          <div className="flex shrink-0 items-center border-t border-border bg-background px-1 py-3">
            <button
              type="button"
              onClick={() => setView("document")}
              className="inline-flex items-center gap-1.5 self-start rounded-lg bg-primary px-2.5 py-1.5 text-[12px] font-semibold text-primary-foreground shadow-sm shadow-black/10 ring-1 ring-primary/10 transition hover:opacity-90"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              View full document
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex shrink-0 items-center border-b border-border bg-background px-1 py-3">
            <button
              type="button"
              onClick={() => setView("extracts")}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] font-semibold text-foreground transition hover:bg-accent"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              Back to cited text
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto px-1 py-5">
            <div className="mx-auto max-w-[980px]">
              <PdfSourcePreview
                href={pdfSrc}
                title={title}
                highlights={item.link.highlights}
                initialHighlightId={highlightUuid}
              />
            </div>
          </div>
        </>
      )}
    </section>
  );
};

const DocumentExtractContent: React.FC<{
  content: string;
  highlightUuid: string | null;
  citationColor: string;
  hasStructuredHtml: boolean;
  title: string;
  sourceName?: string;
}> = ({
  content,
  highlightUuid,
  citationColor,
  hasStructuredHtml,
  title,
  sourceName,
}) => {
  if (hasStructuredHtml) {
    return (
      <HtmlSourcePreview
        html={content}
        activeCitationId={highlightUuid}
        title={title}
        sourceName={sourceName}
        iframeCitationColor={citationColor}
      />
    );
  }

  return (
    <SimpleMarkdown
      text={content}
      className="citation-markdown-content"
      tableClassName={SOURCE_TABLE_CLASS_NAME}
      thClassName={SOURCE_TH_CLASS_NAME}
      tdClassName={SOURCE_TD_CLASS_NAME}
      activeCitationId={highlightUuid}
    />
  );
};

